import { geocodeCep, GeocodingResult } from '@/services/geocodingService';
import { areaCoversPoint, hasCells as hasPaintedCellsHelper } from '@/utils/h3Cells';

/** True quando a região foi desenhada no mapa (células H3). */
function hasPaintedCells(range: { cells?: string[] }): boolean {
  return hasPaintedCellsHelper(range.cells);
}

export interface EventSizeSurcharge {
  minGuests: number;
  maxGuests: number;
  surcharge: number;
}

export interface DistanceRange {
  minKm: number;
  maxKm: number;
  deliveryFee: number;
  freeDelivery: boolean;
  /** true quando o fornecedor alterou manualmente o valor gerado. */
  manualEdited?: boolean;
}

/** Contexto de frete: eventos (cardápios fixos) ou encomendas (cardápios selecionáveis). */
export type FreightContext = 'event' | 'order';

export interface CepRange {
  id?: string;                 // UUID da linha no banco
  region: string;
  context?: FreightContext;    // Contexto ao qual a região pertence
  deliveryFee?: number;        // Valor do frete (R$)
  freeDelivery?: boolean;      // Frete grátis?
  deliveryTimeMinutes?: number; // Tempo estimado de entrega em minutos
  isDisabled?: boolean;        // Não entrega nessa região?
  allowPickup?: boolean;       // Permitir retirada no local?
  
  // Adicional por tamanho de evento
  eventSizeSurchargeEnabled?: boolean;
  eventSizeSurcharges?: EventSizeSurcharge[];

  // Faixas de distância (KM)
  distanceRangesEnabled?: boolean;
  distanceRanges?: DistanceRange[];

  // Cobrança por quilômetro (usada principalmente em encomendas)
  perKmEnabled?: boolean;
  perKmRate?: number;          // R$ por km
  perKmBaseFee?: number;       // Taxa base somada ao valor por km
  perKmMinFee?: number;        // Valor mínimo cobrado

  
  // Tipo de área: 'cep' (padrão), 'circle' ou 'cells' (área desenhada em H3)
  type?: 'cep' | 'circle' | 'cells';
  
  // Campos para tipo 'cep'
  start?: string;
  end?: string;
  
  // Campos para tipo 'circle'
  center?: [number, number];   // [lat, lng]
  radiusMeters?: number;       // Raio em metros

  // Campos para tipo 'cells' (área desenhada)
  cells?: string[];            // Índices H3 (resolução 7)
  cellsResolution?: number;
}

export interface DeliveryValidationResult {
  isValid: boolean;
  area?: CepRange;
  deliveryFee?: number;
  estimatedTimeMinutes?: number;
  isDisabled?: boolean;
  coordinates?: GeocodingResult;
  /**
   * True quando não foi possível localizar o endereço com precisão suficiente
   * para conferir as áreas desenhadas/circulares. Nesse caso não afirmamos que
   * a região está fora de cobertura — o frete é confirmado no checkout.
   */
  isUnverified?: boolean;
}


/**
 * Normalize CEP by removing non-numeric characters
 */
export function normalizeCEP(cep: string): string {
  if (!cep) return '';
  return cep.replace(/\D/g, '');
}

/**
 * Raio da Terra em metros
 */
const EARTH_RADIUS_METERS = 6371000;

/**
 * Fator de conversão aproximado de distância em linha reta para distância por estrada (via).
 * Baseado na média urbana/rodoviária onde a estrada é cerca de 30-40% mais longa que a linha reta.
 */
export const ROAD_DISTANCE_FACTOR = 1.35;

/**
 * Estima a distância por estrada com base na distância em linha reta
 */
export function estimateRoadDistance(straightDistanceMeters: number): number {
  return straightDistanceMeters * ROAD_DISTANCE_FACTOR;
}

/**
 * Converte graus para radianos
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calcula a distância entre dois pontos usando a fórmula de Haversine
 * @returns Distância em metros
 */
export function calculateHaversineDistance(
  point1: [number, number],
  point2: [number, number]
): number {
  const [lat1, lng1] = point1;
  const [lat2, lng2] = point2;
  
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
    Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) *
    Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return EARTH_RADIUS_METERS * c;
}

/**
 * Verifica se um ponto está dentro de um círculo, considerando o fator de estrada
 * @param point - Coordenadas do ponto [lat, lng]
 * @param center - Centro do círculo [lat, lng]
 * @param radiusMeters - Raio de atendimento (via estrada) em metros
 */
export function isPointInCircle(
  point: [number, number],
  center: [number, number],
  radiusMeters: number
): boolean {
  const straightDistance = calculateHaversineDistance(point, center);
  const roadDistance = estimateRoadDistance(straightDistance);
  return roadDistance <= radiusMeters;
}

/**
 * Check if a CEP is within a specific CEP range (type 'cep')
 */
export function isCepInCepRange(cep: string, range: CepRange): boolean {
  if (range.type === 'circle' || !range.start || !range.end) {
    return false;
  }
  
  const normalizedCep = normalizeCEP(cep);
  
  if (normalizedCep.length !== 8) {
    return false;
  }
  
  const cepNum = parseInt(normalizedCep, 10);
  const startNum = parseInt(range.start, 10);
  const endNum = parseInt(range.end, 10);
  
  return cepNum >= startNum && cepNum <= endNum;
}

/**
 * Check if a CEP is within a specific range (synchronous, for CEP ranges only)
 * @deprecated Use checkDeliveryAreaCoverage for full support including circles
 */
export function isCepInRange(cep: string, range: CepRange): boolean {
  // Para áreas do tipo 'circle', retorna false (precisa de geocodificação)
  if (range.type === 'circle') {
    return false;
  }
  
  return isCepInCepRange(cep, range);
}

/**
 * Check if a CEP matches any of the provided CEP ranges (synchronous)
 * Only checks CEP type ranges, not circles
 */
export function matchesAnyCepRange(cep: string, ranges: CepRange[]): boolean {
  if (!ranges || ranges.length === 0) {
    return true; // No restrictions means available everywhere
  }
  
  const cepRanges = ranges.filter(r => r.type !== 'circle');
  if (cepRanges.length === 0) {
    return false; // Only circles exist, need async check
  }
  
  return cepRanges.some(range => isCepInCepRange(cep, range));
}

/**
 * Check if a CEP matches any of the provided ranges (synchronous, legacy)
 * @deprecated Use checkDeliveryAreaCoverage for full support including circles
 */
export function matchesAnyRange(cep: string, ranges: CepRange[]): boolean {
  if (!ranges || ranges.length === 0) {
    return true; // No restrictions means available everywhere
  }
  
  // Verificar áreas do tipo CEP
  const matchesCep = ranges
    .filter(r => r.type !== 'circle')
    .some(range => isCepInCepRange(cep, range));
  
  if (matchesCep) {
    return true;
  }
  
  // Se só existem círculos, retornamos false pois precisa de verificação assíncrona
  const hasOnlyCircles = ranges.every(r => r.type === 'circle');
  if (hasOnlyCircles) {
    return false;
  }
  
  return false;
}

/**
 * Encontra a faixa de distância que cobre a distância fornecida (tratando decimais sem lacunas)
 * Estratégia: arredonda a distância para o inteiro inferior, depois tenta encontrar a faixa
 * Exemplo: 10.1 km → arredonda para 10 → 10 cai em [0-10] ou [11-20]?
 * Com arredondamento baixo (floor), 10.1 vira 10, que cai em [0-10].
 * Com valores como 10.9, vira 10, que cai em [0-10].
 * Se a faixa é [0-10] e [11-20], valores 10.0-10.99 caem em [0-10].
 * 
 * Alternativa: forçar match por `distanceKm > maxKm do anterior && distanceKm <= maxKm da atual`
 * Aqui usaremos a segunda abordagem para ser mais explícita.
 */
function findMatchingDistanceRange(distanceKm: number, ranges: DistanceRange[]): DistanceRange | null {
  if (!ranges || ranges.length === 0) return null;
  
  // Ordena as faixas por minKm para garantir cobertura contígua
  const sorted = [...ranges].sort((a, b) => a.minKm - b.minKm);
  
  // Tenta encontrar a faixa exata
  for (const range of sorted) {
    if (distanceKm >= range.minKm && distanceKm <= range.maxKm) {
      return range;
    }
  }
  
  // Se não encontrar exato, tenta arredondar para baixo e procurar novamente
  // Isso evita lacunas entre 10.01 e 10.99 quando há faixas [0-10] e [11-20]
  const floorDistance = Math.floor(distanceKm);
  if (floorDistance !== distanceKm) {
    for (const range of sorted) {
      if (floorDistance >= range.minKm && floorDistance <= range.maxKm) {
        return range;
      }
    }
  }
  
  return null;
}

/**
 * Frete por quilômetro: taxa base + (R$/km × distância), respeitando o valor mínimo.
 * Retorna null quando a região não usa cobrança por km ou quando não há distância confiável.
 */
export function computePerKmFee(range: CepRange, distanceKm?: number): number | null {
  if (!range.perKmEnabled) return null;
  if (distanceKm == null || !Number.isFinite(distanceKm)) return null;

  const rate = Number(range.perKmRate) || 0;
  const base = Number(range.perKmBaseFee) || 0;
  const min = Number(range.perKmMinFee) || 0;
  const raw = base + rate * Math.max(0, distanceKm);

  return Math.round(Math.max(raw, min) * 100) / 100;
}

/**
 * Calcula o frete final considerando o frete base, cobrança por km,
 * faixas de distância e adicional por tamanho de evento.
 */
function calculateFinalFee(range: CepRange, guestCount?: number, distanceKm?: number): number {
  let fee = 0;

  const perKmFee = computePerKmFee(range, distanceKm);

  if (perKmFee != null) {
    // 0. Cobrança por quilômetro tem prioridade quando configurada
    fee = range.freeDelivery ? 0 : perKmFee;
  } else if (range.distanceRangesEnabled && distanceKm != null && range.distanceRanges && range.distanceRanges.length > 0) {
    // 1. Faixas de distância
    const matchingRange = findMatchingDistanceRange(distanceKm, range.distanceRanges);
    if (matchingRange) {
      fee = matchingRange.freeDelivery ? 0 : matchingRange.deliveryFee;
    } else {
      fee = range.freeDelivery ? 0 : (range.deliveryFee ?? 0);
    }
  } else {
    // 2. Frete fixo da região
    fee = range.freeDelivery ? 0 : (range.deliveryFee ?? 0);
  }
  
  // 3. Adiciona sobretaxa por tamanho de evento se habilitado
  if (range.eventSizeSurchargeEnabled && guestCount != null && range.eventSizeSurcharges) {
    const surcharge = range.eventSizeSurcharges.find(
      s => guestCount >= s.minGuests && guestCount <= s.maxGuests
    );
    if (surcharge) {
      fee += surcharge.surcharge;
    }
  }
  
  return fee;
}


/**
 * Verifica assincronamente se um CEP está coberto por alguma área de entrega
 * Suporta tanto áreas por CEP quanto áreas circulares
 */
export async function checkDeliveryAreaCoverage(
  cep: string,
  ranges: CepRange[],
  guestCount?: number
): Promise<DeliveryValidationResult> {
  if (!ranges || ranges.length === 0) {
    return { isValid: true }; // Sem restrições = entrega em qualquer lugar
  }
  
  const normalizedCep = normalizeCEP(cep);
  
  if (normalizedCep.length !== 8) {
    return { isValid: false };
  }
  
  // Coletar TODAS as áreas que cobrem o CEP para escolher a melhor (menor frete)
  const matchingResults: DeliveryValidationResult[] = [];

  // 1. Verificar áreas do tipo CEP (mais rápido)
  const cepRanges = ranges.filter(r => r.type !== 'circle' && !hasPaintedCells(r));
  for (const range of cepRanges) {
    if (isCepInCepRange(normalizedCep, range)) {
      const fee = calculateFinalFee(range, guestCount);
      matchingResults.push({
        isValid: !range.isDisabled,
        area: range,
        deliveryFee: fee,
        estimatedTimeMinutes: range.deliveryTimeMinutes,
        isDisabled: range.isDisabled,
      });
    }
  }
  
  // 2. Verificar áreas circulares e desenhadas (requer geocodificação)
  const circleRanges = ranges.filter(r => r.type === 'circle' && !hasPaintedCells(r));
  const cellRanges = ranges.filter(hasPaintedCells);
  let coordinates: GeocodingResult | null = null;

  if (cellRanges.length > 0) {
    coordinates = await geocodeCep(normalizedCep);
    if (coordinates) {
      const customerPoint: [number, number] = [coordinates.lat, coordinates.lng];
      for (const range of cellRanges) {
        if (areaCoversPoint(range.cells, coordinates.lat, coordinates.lng)) {
          // Distância por estrada a partir do ponto de origem da área (endereço do fornecedor)
          const distanceKm = range.center
            ? estimateRoadDistance(calculateHaversineDistance(customerPoint, range.center)) / 1000
            : undefined;
          matchingResults.push({
            isValid: !range.isDisabled,
            area: range,
            deliveryFee: calculateFinalFee(range, guestCount, distanceKm),
            estimatedTimeMinutes: range.deliveryTimeMinutes,
            isDisabled: range.isDisabled,
            coordinates,
          });
        }
      }

    }
  }

  if (circleRanges.length > 0) {
    coordinates = coordinates ?? (await geocodeCep(normalizedCep));
    

    
    if (coordinates) {
      const customerPoint: [number, number] = [coordinates.lat, coordinates.lng];
      for (const range of circleRanges) {
        if (range.center && range.radiusMeters) {
          const isInCircle = isPointInCircle(customerPoint, range.center, range.radiusMeters);
          if (isInCircle) {
            const straightDistanceMeters = calculateHaversineDistance(customerPoint, range.center);
            const roadDistanceKm = estimateRoadDistance(straightDistanceMeters) / 1000;
            const fee = calculateFinalFee(range, guestCount, roadDistanceKm);
            matchingResults.push({
              isValid: !range.isDisabled,
              area: range,
              deliveryFee: fee,
              estimatedTimeMinutes: range.deliveryTimeMinutes,
              isDisabled: range.isDisabled,
              coordinates,
            });
          }
        }
      }
    } else {
      console.warn('[CEP] Não foi possível geocodificar o CEP:', normalizedCep);
    }
  }

  if (matchingResults.length === 0) {
    const needsGeocode = cellRanges.length > 0 || circleRanges.length > 0;
    const geocodeIsReliable = !!coordinates && (coordinates as GeocodingResult).precision !== 'approximate';

    // Encomendas (contexto "order"): região que não está mapeada pelo fornecedor não é atendida.
    // Antes, uma localização imprecisa do CEP liberava a compra com frete zerado ("Grátis").
    const isOrderCoverage = ranges.length > 0 && ranges.every((r) => r.context === 'order');
    if (isOrderCoverage) {
      return { isValid: false, coordinates: coordinates ?? undefined };
    }

    // Sem geocodificação confiável não é possível afirmar que o endereço está fora da área.
    if (needsGeocode && !geocodeIsReliable) {
      console.warn('[CEP] Geocodificação imprecisa — cobertura não verificada:', normalizedCep);
      return { isValid: true, isUnverified: true, coordinates: coordinates ?? undefined };
    }

    return { isValid: false, coordinates: coordinates ?? undefined };
  }


  // Filtrar áreas válidas (não desabilitadas) e escolher a com menor frete
  const validResults = matchingResults.filter(r => r.isValid);
  
  if (validResults.length > 0) {
    validResults.sort((a, b) => (a.deliveryFee ?? 0) - (b.deliveryFee ?? 0));
    return validResults[0];
  }

  // Se só tem áreas desabilitadas, retornar a primeira
  return matchingResults[0];
}

/**
 * Get regions that cover a specific CEP (synchronous, CEP ranges only)
 */
export function getCoveredRegions(cep: string, ranges: CepRange[]): string[] {
  return ranges
    .filter(range => range.type !== 'circle' && isCepInCepRange(cep, range))
    .map(range => range.region);
}

/**
 * Get all regions that cover a specific CEP (async, includes circles)
 */
export async function getAllCoveredRegions(
  cep: string,
  ranges: CepRange[]
): Promise<{ region: string; area: CepRange }[]> {
  const result: { region: string; area: CepRange }[] = [];
  
  // CEP ranges
  for (const range of ranges.filter(r => r.type !== 'circle')) {
    if (isCepInCepRange(cep, range)) {
      result.push({ region: range.region, area: range });
    }
  }
  
  // Circle ranges
  const circleRanges = ranges.filter(r => r.type === 'circle');
  if (circleRanges.length > 0) {
    const coordinates = await geocodeCep(cep);
    
    if (coordinates) {
      const customerPoint: [number, number] = [coordinates.lat, coordinates.lng];
      
      for (const range of circleRanges) {
        if (range.center && range.radiusMeters) {
          if (isPointInCircle(customerPoint, range.center, range.radiusMeters)) {
            result.push({ region: range.region, area: range });
          }
        }
      }
    }
  }
  
  return result;
}
